import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LocalStorageService } from './local-storage.service';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { ApiConstants } from '../constants/api-url.constant';
import { Router } from '@angular/router';
import { SessionVariable } from '../enum/session-variable';
import { MessageService } from 'primeng/api';
import { OAuthTokenResponse } from '../models/auth-token-response.model';
import { SharedSignalsService } from './shared-signals.service';
import { EnvService } from '../../env.service';
import { ResendOtpRequest } from '../models/resend-otp-request.model';
import { UserProfile } from '../models/user-profile.model';
import {
  LoginRequest,
  InitAuthRequest,
  InitAuthResponse,
  VerifyOtpRequest,
} from '../models/auth.model';
import {
  OrderProcessRequest,
  RequestStatusRequest,
  VerifyNationalIdRequest,
  CnetAuthenticationRequest,
  UnlockAccountRequest,
  lockAccountStatusRequest,
  completeAccountFlowRequest,
  ChangePhoneNumberOtpRequest,
  ResendChangeNumberOtpRequest,
  ChangePhoneNumberLoginRequest,
  ChangePhoneNumberStatusRequest,
  ChangePhoneNumberAuthenticateRequest,
} from '../../checkout/checkout-model';

export const FLOW_SESSION_SECRET_HEADER = 'X-Flow-Session-Secret';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private storageService = inject(LocalStorageService);
  private http = inject(HttpClient);
  private toasterService = inject(MessageService);
  private apiConstants = inject(ApiConstants);
  private transferSignal = inject(SharedSignalsService);
  private router = inject(Router);
  private envService = inject(EnvService);
  private readonly flowSessionSecrets = new Map<string, string>();

  login(request: LoginRequest): Observable<OAuthTokenResponse> {
    const header = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: this.envService.browserBasicAuth,
      requestFor: 'consumer',
    });

    const body = new URLSearchParams();
    body.set('username', request?.username);
    body.set('password', request?.password?.toString());
    body.set('grant_type', 'password');
    body.set('scope', 'ui');

    return this.http
      .post<OAuthTokenResponse>(this.apiConstants.OAUTH_TOKEN(), body.toString(), {
        headers: header,
      })
      .pipe(
        map((res: OAuthTokenResponse) => {
          this.storageService.setItem(SessionVariable.ACCESS_TOKEN, res?.access_token);
          this.storageService.setItem(SessionVariable.REFRESH_TOKEN, res?.refresh_token);
          return res; // return modified response
        }),
        catchError((error) => {
          const errorMsg = error?.error?.error_description || error?.error?.message;
          return throwError(() => errorMsg);
        })
      );
  }

  refreshToken(): Observable<any> {
    const header = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: this.envService.browserBasicAuth,
      refreshtoken: 'true',
    });
    const refresh_token: any = this.storageService.getItem(SessionVariable.REFRESH_TOKEN) || null;
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', refresh_token);
    return this.http
      .post<OAuthTokenResponse>(this.apiConstants.OAUTH_TOKEN(), body.toString(), {
        headers: header,
      })
      .pipe(
        tap((res: OAuthTokenResponse) => {
          // Refresh tokens are rotated: the old one is rejected after this call,
          // so both new tokens must be stored here for every caller.
          this.storageService.setItem(SessionVariable.ACCESS_TOKEN, res?.access_token);
          this.storageService.setItem(SessionVariable.REFRESH_TOKEN, res?.refresh_token);
        })
      );
  }

  InitAuth(request: InitAuthRequest): Observable<InitAuthResponse> {
    return this.http.post<InitAuthResponse>(this.apiConstants.INIT_AUTH(), request).pipe(
      tap((resp: InitAuthResponse) => {
        if (resp) {
          this.storageService.setItem(
            SessionVariable.REGISTRATION_NUMBER,
            `${request.countryCode}${request.mobileNumber}`
          );
          if (this.hasCnetAuthField(resp.requestId)) {
            this.transferSignal?.setRequestId(resp.requestId);
            this.storageService.setItem(SessionVariable.REQ_ID, resp.requestId);
          }

          if (resp.otpReference) {
            this.transferSignal?.setOtpReference(resp.otpReference);
            this.storageService.setItem(SessionVariable.OTP_REF, resp.otpReference);
          }
          this.storageService.setItem(SessionVariable.COUNTRY_CODE, request.countryCode);
          this.storageService.setItem(SessionVariable.MOBILE_NUMBER, request.mobileNumber);
          if (resp?.token) {
            this.storageService.setItem(SessionVariable.SIGNUP_TOKEN, resp?.token);
          }
        }
      }),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  verfiyOTP(request: VerifyOtpRequest): Observable<string> {
    return this.http.post(this.apiConstants.VERIFY_OTP(), request, { responseType: 'text' }).pipe(
      catchError((err) => {
        const errorMsg = err?.error?.message || err?.message;
        this.showMessage('error', errorMsg);
        return throwError(() => err);
      })
    );
  }

  resendOTP(request: ResendOtpRequest): Observable<any> {
    return this.http.post(this.apiConstants.RESEND_OTP(), request);
  }

  verifyNationalId(request: VerifyNationalIdRequest): Observable<any> {
    return this.http.post(this.apiConstants.NATIONAL_ID(), request);
  }
  cnetAuthentication(request: CnetAuthenticationRequest): Observable<any> {
    if (!this.hasCnetAuthField(request?.civilId) || !this.hasCnetAuthField(request?.requestId)) {
      return throwError(() => ({
        message: 'Missing civilId or requestId for CINET authentication',
        errorCode: 'INVALID_CNET_AUTH',
      }));
    }

    return this.http.post(this.apiConstants.CNET_AUTHENTICATION(), request);
  }

  cnetAuthenticationStatus(civilId: any, referenceNo: any, UserId?: any): Observable<any> {
    let url = this.apiConstants.CNET_AUTHENTICATION_STATUS(civilId, referenceNo);

    if (UserId !== undefined) {
      url = `${url}&userId=${UserId}`;
    }

    return this.http.get(url);
  }

  cnetAuthenticationToken(requestId: any, userId: any): Observable<any> {
    return this.http.get(this.apiConstants.CNET_AUTHENTICATION_TOKEN(requestId, userId), {
      responseType: 'text',
    });
  }

  requestStatus(request: RequestStatusRequest): Observable<any> {
    return this.http.post(this.apiConstants.REQUEST_STATUS(), request);
  }

  customerInfo(request: any): Observable<any> {
    return this.http.post(this.apiConstants.CUSTOMER_INFO(), request);
  }

  getCreditCard(): Observable<any> {
    return this.http.get(this.apiConstants.CREDIT_CARD());
  }

  saveCreditCard(request: any): Observable<any> {
    return this.http.post(this.apiConstants.CREDIT_CARD(), request);
  }

  orderProcess(request: OrderProcessRequest): Observable<any> {
    return this.http.post(this.apiConstants.ORDER_PROCESS(), request);
  }

  payInstallmentOnKnet(request: any): Observable<any> {
    return this.http.post(this.apiConstants.INSTALLMENT_PAY_KNET(), request);
  }

  payStandaloneInstallmentOnKnet(
    paymentToken: string,
    installmentIds?: number[] | null
  ): Observable<any> {
    return this.http.post(this.apiConstants.STANDALONE_INSTALLMENT_PAY_KNET(paymentToken), installmentIds);
  }

  payByApplePay(request: any): Observable<any> {
    return this.http.post(this.apiConstants.INSTALLMENT_PAY_APPLE(), request);
  }

  retrieveCharge(chargeId: string, paymentType: string): Observable<any> {
    const API_URL =
      paymentType == 'knet'
        ? this.apiConstants.PAYMENT_STATUS(chargeId)
        : this.apiConstants.RETRIEVE_CHARGE(chargeId);

    return this.http.get(API_URL);
  }

  showMessage(severityType: string, toastMessage: string) {
    this.toasterService.clear();
    this.toasterService.add({ severity: severityType, detail: toastMessage });
  }

  getCnetRenewalStatus(): Observable<any> {
    return this.http.get(this.apiConstants.GET_CNET_RENEWAL_STATUS());
  }

  getUserProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(this.apiConstants.GET_USER_PROFILE()).pipe(
      map((profile: UserProfile) => {
        this.storageService.setItem(SessionVariable.AUTH_PROFILE, JSON.stringify(profile));
        this.transferSignal.setIsLogin(true);
        this.storageService.setItem(SessionVariable.USER_ID, profile?.id);

        if (profile?.nationalId) {
          this.storageService.setItem(SessionVariable.NATIONAL_ID, profile.nationalId);
          this.transferSignal.setNationalId(profile.nationalId);
        }

        return {
          ...profile,
          cinetAuthenticationStatus: profile.cinetAuthenticationStatus || 'INACTIVE',
        };
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  private handleKycRedirect(profile: any) {
    if (this.router.url.includes('/kyc')) {
      return;
    }

    const accessToken = this.storageService.getItem<string>(SessionVariable.ACCESS_TOKEN) || '';
    const locale = this.storageService.getItem<string>(SessionVariable.LANGUAGE) || 'en-US';
    const theme = this.storageService.getItem<string>('theme') || 'light';

    const ua = window.navigator.userAgent.toLowerCase();
    let platform = 'android';
    if (/iphone|ipad|ipod/.test(ua)) {
      platform = 'ios';
    } else if (/android/.test(ua)) {
      platform = 'android';
    }

    const requiredVersion = profile.kycVersionRequired || 4;

    const payload = {
      type: 'BOOTSTRAP',
      protocolVersion: 1,
      accessToken: accessToken,
      locale: locale,
      theme: theme,
      platform: platform,
      requiredVersion: requiredVersion,
      profile: {
        nationalityIso3: profile.nationalityIso3,
        nationalityName: profile.nationalityName,
        fullName: profile.fullName,
        fullNameArabic: profile.fullNameArabic,
        kyc: profile.kyc,
        kycVersionRequired: profile.kycVersionRequired
      }
    };

    console.log('[AuthService] KYC REQUIRED intercepted. Saving payload & redirecting:', {
      locale,
      theme,
      platform,
      requiredVersion
    });

    // Save payload to local storage so the lazy-loaded KycComponent can read it immediately on load
    localStorage.setItem('kyc_bootstrap_payload', JSON.stringify(payload));

    if ((window as any).talyKycBootstrap) {
      try {
        (window as any).talyKycBootstrap(payload);
      } catch (err) {
        console.error('[AuthService] talyKycBootstrap call failed:', err);
      }
    } else {
      console.warn('[AuthService] window.talyKycBootstrap is not defined on window yet.');
    }

    // Parse order ID from checkout URL if present to redirect to checkout-specific route
    const match = this.router.url.match(/\/secure-checkout\/([^/]+)/);
    if (match) {
      const tokenOrderId = match[1];
      this.router.navigate(['/secure-checkout', tokenOrderId, 'kyc']);
    } else {
      this.router.navigate(['/kyc']);
    }
  }
  UnlockAccountRequest(request: UnlockAccountRequest): Observable<any> {
    return this.http
      .post(this.apiConstants.AUTHENTICATE_LOCKED_ACCOUNT(), request)
      .pipe(tap((resp) => this.rememberFlowSessionSecret(resp)));
  }
  lockedAccountStatus(request: lockAccountStatusRequest): Observable<any> {
    return this.http.post(this.apiConstants.LOCKED_ACCOUNT_STATUS(), request, {
      headers: this.flowSessionHeaders(request.flowSessionId),
    });
  }
  completeAccountFlow(request: completeAccountFlowRequest): Observable<any> {
    return this.http
      .post(this.apiConstants.COMPLETE_ACCOUNT_FLOW(), request, {
        headers: this.flowSessionHeaders(request.flowSessionId),
      })
      .pipe(tap((resp) => this.forgetFlowSessionSecretWhenCompleted(request.flowSessionId, resp)));
  }

  changePhoneNumberAuthenticate(request: ChangePhoneNumberAuthenticateRequest): Observable<any> {
    return this.http
      .post(this.apiConstants.CHANGE_PHONE_NUMBER_AUTHENTICATE(), request)
      .pipe(tap((resp) => this.rememberFlowSessionSecret(resp)));
  }

  changePhoneNumberStatus(request: ChangePhoneNumberStatusRequest): Observable<any> {
    return this.http.post(this.apiConstants.CHANGE_PHONE_NUMBER_STATUS(), request, {
      headers: this.flowSessionHeaders(request.flowSessionId),
    });
  }

  changePhoneNumberLogin(request: ChangePhoneNumberLoginRequest): Observable<any> {
    return this.http.post(this.apiConstants.CHANGE_PHONE_NUMBER_LOGIN(), request, {
      headers: this.flowSessionHeaders(request.flowSessionId),
    });
  }

  changePhoneNumberOtp(request: ChangePhoneNumberOtpRequest): Observable<any> {
    return this.http
      .post(this.apiConstants.CHANGE_PHONE_NUMBER_OTP(), request, {
        headers: this.flowSessionHeaders(request.flowSessionId),
      })
      .pipe(tap((resp) => this.forgetFlowSessionSecretWhenCompleted(request.flowSessionId, resp)));
  }

  resendChangeNumberOtp(request: ResendChangeNumberOtpRequest): Observable<any> {
    return this.http.post(this.apiConstants.RESEND_CHANGE_NUMBER_OTP(), request, {
      headers: this.flowSessionHeaders(request.flowSessionId),
    });
  }

  // The recovery start call returns flowSessionSecret once; every later call of that flow must send it as
  // X-Flow-Session-Secret, so knowing the flowSessionId alone is not enough to continue someone's flow.
  // Kept in memory only, like the flowSessionId the components hold, and never logged or put in a URL.
  private rememberFlowSessionSecret(resp: any) {
    // Only one recovery flow runs at a time, so a new start replaces any earlier secret.
    this.flowSessionSecrets.clear();
    if (resp?.flowSessionId && resp?.flowSessionSecret) {
      this.flowSessionSecrets.set(resp.flowSessionId, resp.flowSessionSecret);
    }
  }

  private forgetFlowSessionSecretWhenCompleted(flowSessionId: string, resp: any) {
    if (resp?.status === 'COMPLETED') {
      this.flowSessionSecrets.delete(flowSessionId);
    }
  }

  // No header when no secret is held: a backend that does not issue secrets yet ignores it either way.
  private flowSessionHeaders(flowSessionId: string): HttpHeaders {
    const secret = flowSessionId ? this.flowSessionSecrets.get(flowSessionId) : undefined;
    return secret ? new HttpHeaders({ [FLOW_SESSION_SECRET_HEADER]: secret }) : new HttpHeaders();
  }

  private hasCnetAuthField(value: unknown): boolean {
    if (value == null || value === '') {
      return false;
    }

    const normalized = String(value).trim();

    return !!normalized && normalized !== 'null' && normalized !== 'undefined';
  }
}
